import { useState } from "react";
import { Link } from "react-router-dom";
import { allPosts, newsCategories, type BlogCategory } from "../../../mocks/newsData";

export default function NewsGrid() {
  const [activeCategory, setActiveCategory] = useState<BlogCategory>("All");
  const [visibleCount, setVisibleCount] = useState(6);

  const nonFeatured = allPosts.filter((p) => !p.featured);
  const filtered =
    activeCategory === "All"
      ? nonFeatured
      : nonFeatured.filter((p) => p.category === activeCategory);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const categoryColours: Record<BlogCategory, string> = {
    "All": "bg-cl-lime text-cl-dark",
    "Environment": "bg-emerald-100 text-emerald-800",
    "Tips & Guides": "bg-amber-100 text-amber-800",
    "Company News": "bg-sky-100 text-sky-800",
    "Industry": "bg-rose-100 text-rose-800",
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        {/* Category filter pills */}
        <div className="flex items-center flex-wrap gap-2.5 mb-12">
          {newsCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setVisibleCount(6); }}
              className={`px-5 py-2 rounded-full text-sm font-black border-2 transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-cl-dark text-white border-cl-dark cartoon-shadow-sm"
                  : "bg-white text-cl-dark border-cl-dark/20 hover:border-cl-dark"
              }`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto text-sm text-cl-dark/40 font-semibold">{filtered.length} articles</span>
        </div>

        {/* Article grid */}
        {visible.length === 0 ? (
          <div className="text-center py-20 text-cl-dark/40 font-semibold">
            <i className="ri-newspaper-line text-4xl mb-3 block"></i>
            No articles in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {visible.map((post, idx) => (
            <Link
                key={post.id}
                to={`/news/${post.slug}`}
                className={`group block cursor-pointer ${idx === 0 && activeCategory === "All" ? "sm:col-span-2 lg:col-span-1" : ""}`}
              >
                <article className="bg-white rounded-2xl overflow-hidden cartoon-card h-full flex flex-col transition-transform duration-300 group-hover:-translate-y-1">
                  <div className="relative overflow-hidden" style={{ height: "200px" }}>
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 object-top"
                    />
                    <span className={`absolute top-3 left-3 text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full border-2 border-cl-dark/10 ${categoryColours[post.category]}`}>
                      {post.category}
                    </span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-cl-green text-xs font-black uppercase tracking-widest mb-2">{post.date} · {post.readTime}</p>
                    <h3 className="text-cl-dark font-black text-base leading-snug group-hover:text-cl-green transition-colors mb-2 flex-1">
                      {post.title}
                    </h3>
                    <p className="text-cl-dark/60 text-sm leading-relaxed font-semibold line-clamp-2 mb-4">{post.excerpt}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs font-semibold bg-cl-tint text-cl-green px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Author */}
                    <div className="flex items-center gap-2.5 border-t-2 border-cl-dark/5 pt-4 mt-auto">
                      <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-cl-dark flex-shrink-0">
                        <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-black text-cl-dark truncate">{post.author.name}</p>
                        <p className="text-xs text-cl-dark/50 font-semibold truncate">{post.author.role}</p>
                      </div>
                      <span className="text-cl-green font-black text-xs flex items-center gap-1 whitespace-nowrap">
                        Read <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </article>
            </Link>
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="text-center">
            <button
              onClick={() => setVisibleCount((c) => c + 6)}
              className="inline-flex items-center gap-2 bg-cl-dark text-white font-black text-sm px-8 py-3.5 rounded-2xl cartoon-btn cursor-pointer"
            >
              Load more articles
              <i className="ri-arrow-down-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
